import styled, { css } from "styled-components";
import tokens from "../../theme/tokens";

export const StatusBar = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.spacing[4]};
  flex-wrap: wrap;

  margin: 0 0 ${tokens.spacing[3]} 0;
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]};

  background: ${tokens.surface.surface};
  border: 1px solid ${tokens.border.light};
  border-radius: ${tokens.radius.lg};

  font-family: ${tokens.typography.fontFamily};
  color: ${tokens.text.primary};
`;

export const StatusLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing[3]};
  flex-wrap: wrap;
`;

export const StatusRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing[2]};
`;

const pillVariant = {
  ready: css`
    color: ${tokens.status.ready.fg};
    background: ${tokens.status.ready.bg};
    border-color: ${tokens.status.ready.border};
  `,
  processing: css`
    color: ${tokens.status.processing.fg};
    background: ${tokens.status.processing.bg};
    border-color: ${tokens.status.processing.border};
  `,
  review: css`
    color: ${tokens.status.review.fg};
    background: ${tokens.status.review.bg};
    border-color: ${tokens.status.review.border};
  `,
  failed: css`
    color: ${tokens.status.failed.fg};
    background: ${tokens.status.failed.bg};
    border-color: ${tokens.status.failed.border};
  `,
  neutral: css`
    color: ${tokens.status.neutral.fg};
    background: ${tokens.status.neutral.bg};
    border-color: ${tokens.status.neutral.border};
  `,
};

export const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[1]};
  border: 1px solid transparent;
  border-radius: ${tokens.radius.pill};
  padding: 4px 10px;
  height: 24px;
  font-family: ${tokens.typography.fontFamily};
  font-size: 12px;
  font-weight: ${tokens.typography.weight.semibold};
  line-height: 1;
  white-space: nowrap;
  cursor: ${(p) => (p.$interactive ? "pointer" : "default")};
  appearance: none;
  outline: none;

  ${(p) => pillVariant[p.$variant] || pillVariant.neutral}

  ${(p) =>
    p.$interactive &&
    css`
      &:hover {
        filter: brightness(0.96);
      }
      &:focus-visible {
        box-shadow: 0 0 0 2px ${tokens.brand.primaryLight};
      }
    `}

  ${(p) =>
    p.$muted &&
    css`
      opacity: 0.55;
    `}
`;

export const PillCount = styled.span`
  font-weight: ${tokens.typography.weight.bold};
`;

export const PillLabel = styled.span`
  font-weight: ${tokens.typography.weight.medium};
`;

export const ReadyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[1]};
  padding: 4px 12px;
  height: 26px;
  border-radius: ${tokens.radius.pill};
  border: 1px solid ${tokens.status.ready.border};
  background: ${tokens.status.ready.bg};
  color: ${tokens.status.ready.fg};
  font-size: 12px;
  font-weight: ${tokens.typography.weight.semibold};
`;

/**
 * Matter tier badge (see `MatterTierBadge.js`). Same pill chrome as
 * `ReadyBadge` so the tier reads as a sibling of the status surface:
 *
 *   ai              → ready tone (green)      — best tier
 *   legacy_sorted   → processing tone (blue)  — middle tier
 *   legacy_unsorted → neutral tone (gray)     — worst tier
 */
const tierVariant = {
  ai: css`
    color: ${tokens.status.ready.fg};
    background: ${tokens.status.ready.bg};
    border-color: ${tokens.status.ready.border};
  `,
  legacy_sorted: css`
    color: ${tokens.status.processing.fg};
    background: ${tokens.status.processing.bg};
    border-color: ${tokens.status.processing.border};
  `,
  legacy_unsorted: css`
    color: ${tokens.status.neutral.fg};
    background: ${tokens.status.neutral.bg};
    border-color: ${tokens.status.neutral.border};
  `,
};

export const TierBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[1]};
  padding: 4px 12px;
  height: 26px;
  border-radius: ${tokens.radius.pill};
  border: 1px solid transparent;
  font-family: ${tokens.typography.fontFamily};
  font-size: 12px;
  font-weight: ${tokens.typography.weight.semibold};
  line-height: 1;
  white-space: nowrap;
  cursor: default;

  ${(p) => tierVariant[p.$tier] || tierVariant.legacy_unsorted}
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[1]};
  padding: 6px 14px;
  border-radius: ${tokens.radius.md};
  border: 1px solid ${tokens.status.failed.border};
  background: ${tokens.status.failed.fg};
  color: ${tokens.text.onAccent};
  font-family: ${tokens.typography.fontFamily};
  font-size: 12px;
  font-weight: ${tokens.typography.weight.semibold};
  cursor: pointer;

  &:hover {
    filter: brightness(0.95);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[1]};
  padding: 6px 12px;
  border-radius: ${tokens.radius.md};
  border: 1px solid ${tokens.border.default};
  background: ${tokens.surface.surface};
  color: ${tokens.text.secondary};
  font-family: ${tokens.typography.fontFamily};
  font-size: 12px;
  font-weight: ${tokens.typography.weight.semibold};
  cursor: pointer;

  &:hover {
    background: ${tokens.surface.hover};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const ErrorPanel = styled.div`
  margin: 0 0 ${tokens.spacing[3]} 0;
  border: 1px solid ${tokens.status.failed.border};
  background: ${tokens.status.failed.bg}33;
  border-radius: ${tokens.radius.lg};
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]};
  font-family: ${tokens.typography.fontFamily};
`;

export const ErrorPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.spacing[2]};
  font-size: 13px;
  font-weight: ${tokens.typography.weight.semibold};
  color: ${tokens.status.failed.fg};
`;

export const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[1.5]};
`;

export const ErrorRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.spacing[3]};
  padding: 6px 0;
  border-top: 1px solid ${tokens.border.light};
  font-size: 12px;
  color: ${tokens.text.secondary};

  &:first-child {
    border-top: none;
  }
`;

export const ErrorRowName = styled.span`
  font-weight: ${tokens.typography.weight.medium};
  color: ${tokens.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 50%;
`;

export const ErrorRowMeta = styled.span`
  color: ${tokens.text.muted};
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`;

export const ErrorNotice = styled.div`
  margin-top: ${tokens.spacing[2]};
  font-size: 11px;
  color: ${tokens.text.muted};
  line-height: 1.4;
`;
